import React, { useEffect, useState } from "react";

/**
 * FigmaOAuthCallback
 * Loaded at /oauth/figma/callback after Figma redirects back.
 * Exchanges the code+state for tokens via our backend, then closes the popup
 * and notifies the opener via postMessage.
 */
const FigmaOAuthCallback: React.FC = () => {
  const [status, setStatus] = useState<"processing" | "success" | "error">("processing");
  const [message, setMessage] = useState("Connecting to Figma…");

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code  = params.get("code");
    const state = params.get("state");
    const error = params.get("error");

    if (error) {
      setStatus("error");
      setMessage(`Figma declined: ${error}`);
      window.opener?.postMessage({ type: "figma_oauth_error", error }, "*");
      return;
    }

    if (!code || !state) {
      setStatus("error");
      setMessage("Missing code or state in callback URL.");
      window.opener?.postMessage({ type: "figma_oauth_error", error: "missing_params" }, "*");
      return;
    }

    const authToken = localStorage.getItem("authToken") || localStorage.getItem("token");

    fetch("/api/mcp/figma/oauth/exchange", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
      },
      body: JSON.stringify({ code, state }),
    })
      .then(r => r.json())
      .then(data => {
        if (data.success) {
          setStatus("success");
          setMessage(`Figma connected — ${data.toolCount} tool${data.toolCount !== 1 ? "s" : ""} ready`);
          window.opener?.postMessage({ type: "figma_oauth_success", server: data.server, toolCount: data.toolCount }, "*");
          setTimeout(() => window.close(), 1500);
        } else {
          setStatus("error");
          setMessage(data.error || "Token exchange failed");
          window.opener?.postMessage({ type: "figma_oauth_error", error: data.error }, "*");
        }
      })
      .catch(err => {
        setStatus("error");
        setMessage(err.message);
        window.opener?.postMessage({ type: "figma_oauth_error", error: err.message }, "*");
      });
  }, []);

  const icon   = status === "processing" ? "⏳" : status === "success" ? "✅" : "❌";
  const color  = status === "error" ? "var(--lyra-color-danger)" : status === "success" ? "var(--lyra-color-success)" : "var(--app-text-2)";
  const bgColor = status === "error" ? "var(--lyra-color-danger-bg)" : status === "success" ? "var(--lyra-color-success-bg)" : "var(--lyra-color-surface-2)";

  return (
    <div style={{
      minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center",
      background: bgColor, fontFamily: "'DM Sans', system-ui, sans-serif",
    }}>
      <div style={{
        textAlign: "center", padding: "32px 40px",
        background: "var(--lyra-color-surface)", borderRadius: 16,
        boxShadow: "0 4px 24px rgba(0,0,0,0.08)",
        maxWidth: 380,
      }}>
        <div style={{ fontSize: 40, marginBottom: 16 }}>{icon}</div>
        <div style={{ fontSize: 15, fontWeight: 600, color, lineHeight: 1.5 }}>{message}</div>
        {status === "success" && (
          <div style={{ fontSize: 12, color: "var(--app-text-3)", marginTop: 8 }}>
            This window will close automatically.
          </div>
        )}
        {status === "error" && (
          <button
            onClick={() => window.close()}
            style={{
              marginTop: 16, padding: "8px 20px", borderRadius: 8,
              background: "var(--app-surface-3)", border: "none", cursor: "pointer",
              fontSize: 13, color: "var(--app-text-2)", fontWeight: 600,
            }}
          >
            Close
          </button>
        )}
      </div>
    </div>
  );
};

export default FigmaOAuthCallback;
